import { CircularProgress, styled } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ConnectionLostMetadata,
  DasEndpointType,
  FeatureListType,
  OtherFeatureType,
  SSSSFeatureType,
} from '../../../types/Device';
import { ProjectFeature } from '../../../types/Project';

import {
  createProjectFeature,
  createProjectSettings,
  deleteProjectFeature,
  getProjectFeature,
  getProjects,
  getProjectSettings,
  updateProjectSettings,
  upsertProjectFeature,
} from '../../../apis/projectApi';

import InformationDialog from '../../../components/Dialog/InformationDialog';
import SettingDialog from '../../../components/Dialog/SettingDialog';
import NormalDropdown from '../../../components/NormalDropdown';

import {
  GeneralSetting,
  GeneralSettingType,
  MainFeature,
  ProjectSetting,
  ProjectSettingType,
} from '../../../types';
import AttendanceDialogContent from './AttendanceDialogContent';
import CapOffDialogContent from './CapOffDialogContent';
import DascartDialogContent from './DascartDialogContent';
import DasLoopDialogContent from './DasLoopDialogContent';
import DataDownloadDialogContent from './DataDownloadDialogContent';
import DisplayNotificationContent from './DisplayNotificationContent';
import Feature from './Feature';
import Header from './Header';
import LostConnectDialogContent from './LostConnectDialogContent';
import PublicAccessDialogContent from './PublicAccessDialogContent';
import WebhookDialogContent from './WebhookDialogContent';
import General from '../general';
import DasVProDialogContent from './content-setting/DasVProDialogContent';
import StreamContent from './StreamContent';

const list: Array<{ id: string; name: string; value: string }> = [
  {
    id: 'light',
    name: 'Light',
    value: 'light',
  },
  {
    id: 'basic',
    name: 'Basic',
    value: 'basic',
  },
  {
    id: 'standard',
    name: 'Standard',
    value: 'standard',
  },
  {
    id: 'premium',
    name: 'Premium',
    value: 'premium',
  },

  {
    id: 'four_s',
    name: 'SSSS',
    value: 'four_s',
  },
];

const Title = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FeatureListInfoPage = () => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [isOpenSettingDialog, setIsOpenSettingDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<
    DasEndpointType | FeatureListType | OtherFeatureType | SSSSFeatureType
  >('dasloop');
  const [disablePa, setDisablePa] = useState(false);
  const [disableVodPreview, setDisableVodPreview] = useState(false);
  const [displayBy, setDisplayBy] = useState('area');
  const [rtmpUrls, setRtmpUrls] = useState<string[]>(['']);
  const [secrets, setSecrets] = useState<
    Array<{ name: string; accessToken?: string }>
  >([
    {
      name: '',
    },
  ]);
  const [capOffMetadata, setCapOffMetadata] = useState<{
    [propertyName: string]: any;
  }>({});
  const [notiMetadata, setNotiMetadata] = useState<{
    [propertyName: string]: any;
  }>({});
  const [dataDownloadMetadata, setDataDownloadMetadata] = useState<{
    [propertyName: string]: any;
  }>({});
  const [webhookMetadata, setWebhookMetadata] = useState<{
    [propertyName: string]: any;
  }>({});
  const [streamMetadata, setStreamMetadata] = useState<{
    [propertyName: string]: any;
  }>({});

  const [mainFeatureType, setMainFeatureType] = useState<MainFeature>('basic');
  const [generalSetting, setGeneralSetting] = useState<GeneralSetting>({
    userLimit: {},
    areaLimit: undefined,
    msgTitle: undefined,
    deviceOfflineInterval: undefined,
    deviceSleepInterval: undefined,
  });

  const {
    data: projectSettingMap,
    isSuccess: isSuccessProjectSetting,
    refetch: refetchProjectSetting,
    isLoading: isLoadingProjectSetting,
  } = useQuery({
    queryKey: ['project-setting'],
    queryFn: async () => {
      const res = await getProjectSettings();
      const rawSettings = res.data.data;

      const localProjectSettingMap: { [index: string]: ProjectSetting } = {};
      rawSettings.forEach((ps: ProjectSetting) => {
        localProjectSettingMap[ps.projectId] = ps;
      });

      return localProjectSettingMap;
    },
    refetchOnWindowFocus: false,
  });

  const projectSetting = useMemo(
    () => projectSettingMap?.[projectId ?? ''],
    [projectSettingMap, projectId],
  );

  useEffect(() => {
    if (isSuccessProjectSetting && projectSetting) {
      setGeneralSetting({
        userLimit: projectSetting.userLimit || {},
        areaLimit: projectSetting.areaLimit,
        msgTitle: projectSetting.msgTitle,
        deviceOfflineInterval: projectSetting.deviceOfflineInterval,
        deviceSleepInterval: projectSetting.deviceSleepInterval,
      });
      setMainFeatureType(projectSetting.plan ?? 'basic');
    }
  }, [isSuccessProjectSetting, projectSetting]);

  const handleOnChangeGeneral = async (
    type: GeneralSettingType,
    metadata: any,
  ) => {
    if (!projectId) return;

    try {
      let updatedData = { ...generalSetting };
      switch (type) {
        case 'user-limit':
          updatedData.userLimit = { ...generalSetting.userLimit, ...metadata };
          break;
        case 'area-limit':
          updatedData.areaLimit =
            typeof metadata === 'number' ? metadata : undefined;
          break;
        case 'alert-notification':
          updatedData.msgTitle =
            typeof metadata === 'string' ? metadata : undefined;
          break;
        case 'offline-detection':
          updatedData = { ...updatedData, ...metadata };
          break;
        default:
          return;
      }

      setGeneralSetting(updatedData);
      if (projectSetting) {
        await updateProjectSettings({
          projectId,
          data: updatedData,
          projectSettingId: projectSetting.id,
        });
      } else {
        await createProjectSettings({
          projectId,
          data: updatedData,
        });
      }
      refetchProjectSetting();
      // setErrors([]);
    } catch (error) {
      if (error instanceof Error) {
        // alert(error.message);
      }
    }
  };

  const [connectionLostMetadata, setConnectionLostMetadata] =
    useState<ConnectionLostMetadata>({ max: 5, interval: 5 });
  const [mode, setMode] = useState<ProjectSettingType>('feature');
  const { data: project } = useQuery({
    queryKey: ['get-projects', projectId],
    queryFn: async () => {
      const res = await getProjects({ id: projectId });
      return res.data.data.find((p) => p.id === projectId) || null;
    },
    enabled: !!projectId,
    refetchOnWindowFocus: false,
  });

  const {
    data: projectFeature,
    refetch: refetchProjectFeature,
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey: ['get-feature-list', projectId],
    queryFn: async () => {
      const res = await getProjectFeature({
        projectId: projectId as string,
      });
      if (res.data.data.length > 0) {
        const featureListMap = res.data.data.reduce<{
          [type: string]: ProjectFeature;
        }>((prev, curr) => {
          prev[curr.type] = curr;
          return prev;
        }, {});
        return featureListMap;
      }
      return {};
    },
    enabled: !!projectId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isSuccess && projectFeature) {
      if (projectFeature['tunnel']?.metadata?.displayBy) {
        setDisplayBy(projectFeature['tunnel'].metadata.displayBy);
      }
      if (projectFeature['dascart']?.metadata?.rtmpUrls) {
        setRtmpUrls(projectFeature['dascart'].metadata.rtmpUrls);
      }
      if (projectFeature['public_access']?.metadata?.secrets) {
        setSecrets(projectFeature['public_access'].metadata.secrets);
      }
      if (projectFeature['dasloop']?.metadata.disablePa) {
        setDisablePa(projectFeature['dasloop']?.metadata.disablePa);
      }
      if (projectFeature['four_s']?.metadata.disablePa) {
        setDisablePa(projectFeature['four_s']?.metadata.disablePa);
      }
      if (projectFeature['stream']?.metadata?.vodPreview) {
        setDisableVodPreview(projectFeature['stream']?.metadata.vodPreview);
      }

      const { metadata } = projectFeature['four_s'] || {};
      if (metadata) {
        const dataDownloadMetadata: { [propertyName: string]: any } = {};

        if ('generalReport' in metadata) {
          dataDownloadMetadata.generalReport = metadata.generalReport;
        }
        if ('attendanceReport' in metadata) {
          dataDownloadMetadata.attendanceReport = metadata.attendanceReport;
        }
        if ('vod' in metadata) {
          dataDownloadMetadata.vod = metadata.vod;
        }
        if ('workTimeReport' in metadata) {
          dataDownloadMetadata.workTimeReport = metadata.workTimeReport;
        }
      }

      if (projectFeature['cap_off']?.metadata) {
        setCapOffMetadata(projectFeature['cap_off']?.metadata);
      }
      if (
        projectFeature['report']?.metadata &&
        mainFeatureType === 'standard'
      ) {
        setDataDownloadMetadata(projectFeature['report']?.metadata);
      }
      if (projectFeature['notification']?.metadata) {
        setNotiMetadata(projectFeature['notification']?.metadata);
      }
      if (projectFeature['connection_lost']?.metadata) {
        setConnectionLostMetadata(projectFeature['connection_lost']?.metadata);
      }
      if (projectFeature['webhook']?.metadata) {
        setNotiMetadata(projectFeature['webhook']?.metadata);
      }
    }
  }, [isSuccess, projectFeature]);

  useEffect(() => {
    return () => {
      setMainFeatureType('standard');
    };
  }, []);

  useEffect(() => {
    if (projectFeature?.['four_s']) {
      setMainFeatureType('four_s');
    }
  }, [projectFeature]);

  let dialogTitle = '';
  switch (selectedType) {
    case 'tunnel':
      dialogTitle = 'Tunnel Settings';
      break;
    case 'dascart':
      dialogTitle = 'Dascart Settings';
      break;
    case 'public_access':
      dialogTitle = 'Access Token Settings';
      break;
    case 'dasloop':
    case 'SmartMonitoringDevices':
      dialogTitle = 'DasLoop Settings';
      break;
    case 'cap_off':
      dialogTitle = 'Cap Off Settings';
      break;
    case 'report':
      dialogTitle = 'Data Download Settings';
      break;
    case 'notification':
      dialogTitle = 'Notification Settings';
      break;
    case 'connection_lost':
      dialogTitle = 'Lost Connection Settings';
      break;
    case 'dasvpro':
      dialogTitle = 'DasVPro';
      break;
    case 'webhook':
      dialogTitle = 'WebHook Settings';
      break;
    case 'stream':
      dialogTitle = 'Stream Settings';
      break;
  }

  let metadataSaveable = true;
  if (selectedType === 'dascart') {
    metadataSaveable = rtmpUrls.reduce(
      (prev, url) => (prev = prev && !!url),
      true,
    );
  } else if (selectedType === 'public_access') {
    metadataSaveable = secrets.reduce(
      (prev, secret) => (prev = prev && !!secret.name),
      true,
    );
  } else if (selectedType === 'report') {
    metadataSaveable = Object.values(dataDownloadMetadata).some(
      (d) => d === true,
    );
  }

  const handleOnChange = async (
    type:
      | DasEndpointType
      | FeatureListType
      | OtherFeatureType
      | SSSSFeatureType,
    checked: boolean,
  ) => {
    setSelectedType(type);

    if (projectId && projectFeature) {
      if (checked === true) {
        const metadata: { [key: string]: any } = {};
        if (type === 'tunnel') {
          metadata.displayBy = 'area';
          // setIsOpenSettingDialog(true);
        } else if (type === 'dascart') {
          setIsOpenSettingDialog(true);
        } else if (type === 'public_access') {
          setIsOpenSettingDialog(true);
        }
        if (type === 'connection_lost') {
          metadata.interval = 5;
          metadata.max = 5;
        }
        if (type === 'stream') {
          metadata.dasVProEnable = checked;
        }
        if (type === 'dasvpro') {
          metadata.vodPreview = checked;
        }

        if (type !== 'public_access') {
          await createProjectFeature({
            projectId,
            data: {
              type,
              metadata,
            },
          });
        }
      } else {
        await deleteProjectFeature({
          projectId,
          data: {
            featureId: projectFeature[type].id,
          },
        });
      }
      refetchProjectFeature();
    }
  };

  const handleOnChangeMetadata = async (
    type:
      | DasEndpointType
      | FeatureListType
      | OtherFeatureType
      | SSSSFeatureType,
    key: string,
    value: any,
  ) => {
    if (projectId && projectFeature?.[type]) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(async () => {
        await upsertProjectFeature({
          projectId,
          data: {
            featureId: projectFeature[type].id,
            metadata: {
              [key]: value,
            },
          },
        });
        refetchProjectFeature();
      }, 1000);
    }
  };

  const handleOnChangeMetadataObject = async (
    type:
      | DasEndpointType
      | FeatureListType
      | OtherFeatureType
      | SSSSFeatureType,
    metadata: any,
  ) => {
    if (projectId && projectFeature?.[type]) {
      await upsertProjectFeature({
        projectId,
        data: {
          featureId: projectFeature[type].id,
          metadata,
        },
      });
      refetchProjectFeature();
    }
  };

  const handleOnSave = async () => {
    if (selectedType === 'tunnel') {
      handleOnChangeMetadata('tunnel', 'displayBy', displayBy);
    } else if (selectedType === 'dascart') {
      handleOnChangeMetadata('dascart', 'rtmpUrls', rtmpUrls);
    } else if (selectedType === 'public_access') {
      if (projectFeature?.['public_access']?.metadata?.secrets) {
        handleOnChangeMetadata('public_access', 'secrets', secrets);
      } else {
        await createProjectFeature({
          projectId: projectId as string,
          data: {
            type: 'public_access',
            metadata: {
              secrets,
            },
          },
        });
      }
    } else if (
      selectedType === 'dasloop' ||
      selectedType === 'SmartMonitoringDevices'
    ) {
      if (selectedType === 'dasloop') {
        handleOnChangeMetadata(selectedType, 'disablePa', disablePa);
      } else {
        handleOnChangeMetadata('four_s', 'disablePa', disablePa);
      }
    } else if (selectedType === 'cap_off') {
      if (projectFeature?.['cap_off']?.metadata) {
        handleOnChangeMetadataObject('cap_off', capOffMetadata);
      } else {
        await createProjectFeature({
          projectId: projectId as string,
          data: {
            type: 'cap_off',
            metadata: capOffMetadata,
          },
        });
      }
    } else if (selectedType === 'report' || selectedType === 'DataDownload') {
      if (selectedType === 'report') {
        handleOnChangeMetadataObject('report', dataDownloadMetadata);
      } else {
        handleOnChangeMetadataObject('four_s', dataDownloadMetadata);
      }
    } else if (selectedType === 'notification') {
      handleOnChangeMetadataObject('notification', notiMetadata);
    } else if (selectedType === 'connection_lost') {
      handleOnChangeMetadataObject('connection_lost', connectionLostMetadata);
    } else if (selectedType === 'webhook') {
      handleOnChangeMetadataObject('webhook', webhookMetadata);
    } else if (selectedType === 'stream') {
      handleOnChangeMetadataObject('stream', streamMetadata);
    } else if (selectedType === 'dasvpro') {
      handleOnChangeMetadata('stream', 'vodPreview', disableVodPreview);
    }

    setIsOpenSettingDialog(false);
    refetchProjectFeature();
  };

  const handleOnSelectMainFeature = async (mainFeatureType: MainFeature) => {
    setMainFeatureType(mainFeatureType);
    if (mainFeatureType === 'four_s') {
      await createProjectFeature({
        projectId: projectId as string,
        data: {
          type: 'four_s',
          metadata: {
            DigitisedTrackingSystem: true,
            DigitalisedPermitToWorkSystem: true,
            HazardousAreasAccessControl: true,
            'UnsafeActs/DangerousSituationAlertForMobilePlantOperationDangerZone':
              true,
            'UnsafeActs/DangerousSituationAlertForTowerCraneLiftingZone': true,
            SmartMonitoringDevices: true,
            SafetyMonitoringSystem: true,
            ConfinedSpaceMonitoringSystem: true,
            SafetyTrainingWithVirtualRealityTechnology: true,
            DataDownload: true,
          },
        },
      });
    } else if (mainFeatureType === 'light') {
      const keep = new Set(['stream', 'dasloop', 'dastrack', 'dascas']);

      const deletions = Object.entries(projectFeature || {})
        .filter(([featureKey]) => !keep.has(featureKey))
        .map(([, feat]) =>
          deleteProjectFeature({
            projectId: projectId as string,
            data: { featureId: feat.id },
          }),
        );

      await Promise.all(deletions);
    } else {
      if (projectFeature?.['four_s']) {
        await deleteProjectFeature({
          projectId: projectId as string,
          data: {
            featureId: projectFeature['four_s'].id,
          },
        });
      }
    }

    if (projectSetting && projectId) {
      await updateProjectSettings({
        projectId,
        projectSettingId: projectSetting.id,
        data: {
          plan: mainFeatureType,
        },
      });
    }
    refetchProjectSetting();
    refetchProjectFeature();
  };

  return (
    <>
      <InformationDialog
        open
        titleNode={{
          start: (
            <NormalDropdown
              list={list}
              value={mainFeatureType}
              onSelect={(v) => handleOnSelectMainFeature(v as MainFeature)}
            />
          ),
          center: (
            <Title>
              <span>{project?.name}</span>
              <span>
                {project &&
                  format(new Date(project.createdAt), 'yyyy - MM - dd')}
              </span>
            </Title>
          ),
        }}
        closeable
        onClose={() => navigate(-1)}
      >
        <Header
          mode={mode}
          onChange={(val) => {
            setMode(val);
          }}
        />
        {isLoading ||
          (isLoadingProjectSetting && (
            <CircularProgress sx={{ display: 'block', margin: 'auto' }} />
          ))}
        {mode === 'feature' && (
          <Feature
            mainFeatureType={mainFeatureType}
            projectFeature={projectFeature}
            handleOnChange={handleOnChange}
            handleOnChangeMetadata={handleOnChangeMetadata}
            setSelectedType={setSelectedType}
            setIsOpenSettingDialog={setIsOpenSettingDialog}
          />
        )}
        {mode === 'general' && (
          <General
            handleOnChangeGeneral={handleOnChangeGeneral}
            generalSetting={generalSetting}
            mainFeatureType={mainFeatureType}
          />
        )}
      </InformationDialog>
      <SettingDialog
        open={isOpenSettingDialog}
        dialogTitle={dialogTitle}
        saveable={metadataSaveable}
        onCancel={() => setIsOpenSettingDialog(false)}
        onSave={handleOnSave}
      >
        {selectedType === 'tunnel' && (
          <AttendanceDialogContent
            defaultValue={displayBy}
            onChange={(e, v) => setDisplayBy(v)}
          />
        )}
        {selectedType === 'dascart' && (
          <DascartDialogContent
            rtmpUrls={rtmpUrls}
            onChange={(newRtmpUrls) => {
              setRtmpUrls(newRtmpUrls);
            }}
          />
        )}
        {selectedType === 'public_access' && (
          <PublicAccessDialogContent
            secrets={secrets}
            onChange={(newSecrets) => {
              setSecrets(newSecrets);
            }}
          />
        )}
        {selectedType === 'notification' && (
          <DisplayNotificationContent
            metadata={{ ...projectFeature?.['notification']?.metadata }}
            onChange={(propertyName: string, value: any) => {
              if (
                propertyName === 'alertDuration' ||
                propertyName === 'alertAmount'
              ) {
                const localNotiMetadata = { ...notiMetadata };
                if (value === '' || value === undefined) {
                  localNotiMetadata[propertyName] = null;
                } else if (propertyName === 'alertDuration') {
                  localNotiMetadata[propertyName] = Number(value);
                } else if (propertyName === 'alertAmount') {
                  if (value >= 0 && value <= 10) {
                    localNotiMetadata[propertyName] = Number(value);
                  } else {
                    localNotiMetadata[propertyName] = 5;
                  }
                }
                setNotiMetadata(localNotiMetadata);
              }
            }}
          />
        )}
        {selectedType === 'stream' && (
          <StreamContent
            metadata={{ ...projectFeature?.['stream']?.metadata }}
            onChange={(propertyName: string, value: any) => {
              setStreamMetadata((prevData) => ({
                ...prevData,
                [propertyName]: value,
              }));
            }}
          />
        )}
        {selectedType === 'cap_off' &&
          projectFeature?.['cap_off']?.metadata && (
            <CapOffDialogContent
              metadata={{
                ...projectFeature?.['cap_off']?.metadata,
              }}
              onChange={(propertyName: string, value: any) => {
                if (
                  propertyName === 'maxAttempts' ||
                  propertyName === 'maxPeriod'
                ) {
                  const localCapOffMetadata = { ...capOffMetadata };

                  if (value === '' || value === undefined) {
                    localCapOffMetadata[propertyName] = null;
                  } else {
                    localCapOffMetadata[propertyName] = Number(value);
                  }
                  setCapOffMetadata(localCapOffMetadata);
                }
              }}
            />
          )}
        {selectedType === 'connection_lost' &&
          projectFeature?.['connection_lost']?.metadata && (
            <LostConnectDialogContent
              metadata={{
                ...projectFeature?.['connection_lost']?.metadata,
              }}
              onChange={(propertyName: string, value: any) => {
                if (propertyName === 'max' || propertyName === 'interval') {
                  const localMetadata = { ...connectionLostMetadata };
                  if (propertyName === 'interval') {
                    if (value > 1 || value <= 99) {
                      localMetadata.interval = value;
                    }
                  } else if (propertyName === 'max') {
                    if (value > 0) {
                      localMetadata.max = value;
                    }
                  }
                  setConnectionLostMetadata(
                    localMetadata as ConnectionLostMetadata,
                  );
                }
              }}
            />
          )}
        {(selectedType === 'report' || selectedType === 'DataDownload') &&
          projectFeature?.['report']?.metadata && (
            <DataDownloadDialogContent
              metadata={{
                ...projectFeature['report']?.metadata,
                ...dataDownloadMetadata,
              }}
              onChange={(propertyName: string, value: any) => {
                if (propertyName === 'allAttendance') {
                  setDataDownloadMetadata({
                    ...dataDownloadMetadata,
                    attendanceReport: value,
                    workTimeReport: value,
                  });
                } else {
                  setDataDownloadMetadata((prevData) => ({
                    ...prevData,
                    [propertyName]: value,
                  }));
                }
              }}
            />
          )}
        {selectedType === 'webhook' && (
          <WebhookDialogContent
            metadata={{ ...projectFeature?.['webhook']?.metadata }}
            onChange={(propertyName: string, value: string) => {
              const localWebhookMetadata = { ...webhookMetadata };
              if (propertyName === 'limit') {
                if (value === null || value === undefined) {
                  localWebhookMetadata[propertyName] = '';
                } else {
                  localWebhookMetadata[propertyName] = Number(value);
                }
              }
              if (!webhookMetadata?.event) {
                const addEvent = {
                  events: [],
                };
                setWebhookMetadata({ ...localWebhookMetadata, ...addEvent });
              } else {
                setWebhookMetadata(localWebhookMetadata);
              }
            }}
          />
        )}
        {(selectedType === 'dasloop' ||
          selectedType === 'SmartMonitoringDevices') &&
          (projectFeature?.['dasloop']?.metadata ||
            projectFeature?.['four_s']?.metadata) && (
            <DasLoopDialogContent
              metadata={{
                ...projectFeature['dasloop']?.metadata,
                ...projectFeature['four_s']?.metadata,
                disablePa,
              }}
              onChange={(propertyName: string, value: any) => {
                if (propertyName === 'disablePa') {
                  setDisablePa(value);
                }
              }}
            />
          )}
        {selectedType === 'dasvpro' && projectFeature?.['stream']?.metadata && (
          <DasVProDialogContent
            metadata={{
              ...projectFeature['stream']?.metadata,
              vodPreview: disableVodPreview,
            }}
            onChange={(propertyName: string, value: any) => {
              if (propertyName === 'vodPreview') {
                setDisableVodPreview(value);
              }
            }}
          />
        )}
      </SettingDialog>
    </>
  );
};

export default FeatureListInfoPage;
